import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Divider,
} from '@mui/material';
import { eventApi } from '../services/api';
import {
  setupTracking,
  trackConversion,
  TrackingConfig,
  TrackingScript,
} from '../tracking/tracking';
import { useTranslation } from '../i18n/TranslationContext';
import LanguageSelector from './LanguageSelector';

interface InstalmentDetails {
  id: number;
  sequence: number;
  total_instalments: number;
  amount: string | number;
  due_date: string;
  payment_status: 'pending' | 'paid' | 'overdue' | 'canceled';
  payment_method: 'paypal' | 'stripe' | 'bonifico';
  event: { id: number; name: string; multilingual_enabled?: boolean };
  customer: { name: string; lastname: string; email: string };
  tracking_config?: TrackingConfig | null;
  tracking_scripts_list?: TrackingScript[];
}

const formatAmount = (a: string | number) => `€${Number(a).toFixed(2)}`;

const PayInstalmentPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t, fmtDate, setForcedDefault } = useTranslation();
  const formatDate = (d: string) => (d ? fmtDate(d + 'T00:00:00') || '-' : '-');
  const instalmentStatusLabel = (st: string) => ({
    pending: t('stati.rata_pending', 'in attesa'),
    paid: t('stati.rata_paid', 'pagata'),
    overdue: t('stati.rata_overdue', 'scaduta'),
    canceled: t('stati.rata_canceled', 'annullata'),
  } as Record<string, string>)[st] ?? st;
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const errorParam = searchParams.get('error');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [instalment, setInstalment] = useState<InstalmentDetails | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      if (!token) {
        setError(t('paga.token_mancante', 'Token mancante'));
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await eventApi.getInstalmentByToken(token);
        if (res?.success) {
          setInstalment(res.data);
          setupTracking(res.data?.tracking_config ?? null, res.data?.tracking_scripts_list ?? []);
          setForcedDefault(!res.data?.event?.multilingual_enabled);
        } else {
          setError(res?.error || t('rata.non_trovata', 'Rata non trovata'));
        }
      } catch (e: any) {
        setError(e?.response?.data?.error || t('rata.err_caricamento', 'Errore caricamento rata'));
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [token]);

  useEffect(() => {
    // Conversione del pagamento rata: tracciata una sola volta per token
    // (la rata risulta "paid" quando si torna qui con status=success)
    if (status !== 'success' || !instalment || !token || instalment.payment_status !== 'paid') {
      return;
    }
    try {
      const dedupeKey = `instalment_conversion_${token}`;
      if (sessionStorage.getItem(dedupeKey)) {
        return;
      }
      sessionStorage.setItem(dedupeKey, '1');
    } catch {
      // senza storage si accetta il rischio di doppio conteggio al reload
    }
    trackConversion(Number(instalment.amount), `INST-${instalment.id}`);
  }, [status, instalment, token]);

  const handlePay = async () => {
    if (!token) return;
    try {
      setSubmitting(true);
      const res = await eventApi.startInstalmentPayment(token);
      if (res?.redirect_url) {
        window.location.href = res.redirect_url;
      } else {
        setError(res?.error || t('paga.err_avvio', 'Errore avvio pagamento'));
        setSubmitting(false);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || t('paga.err_avvio', 'Errore avvio pagamento'));
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Box display="flex" justifyContent="center"><CircularProgress /></Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      {!!instalment?.event?.multilingual_enabled && <LanguageSelector sx={{ mb: 1.5 }} />}
      <Paper elevation={2} sx={{ p: 4 }}>
        {status === 'success' && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {t('rata.successo', 'Pagamento completato con successo. Grazie!')}
          </Alert>
        )}
        {status === 'failed' && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {t('rata.fallito', 'Pagamento non completato.')} {errorParam ? t('rata.dettagli', 'Dettagli: {{err}}', { err: errorParam }) : ''}
          </Alert>
        )}
        {status === 'cancel' && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {t('rata.annullato', 'Pagamento annullato. Puoi riprovare quando vuoi.')}
          </Alert>
        )}

        {error && !instalment && (
          <Alert severity="error">{error}</Alert>
        )}

        {instalment && (
          <>
            <Typography variant="h5" gutterBottom>
              {t('rata.titolo', 'Pagamento rata')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {instalment.event?.name}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mb: 1 }}>
              <Typography variant="body2"><strong>{t('paga.intestatario', 'Intestatario')}:</strong> {instalment.customer.name} {instalment.customer.lastname}</Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2">
                <strong>{t('rata.rata', 'Rata')}:</strong> {t('rata.n_di_m', '{{n}} di {{m}}', { n: instalment.sequence, m: instalment.total_instalments })}
              </Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2"><strong>{t('bonifico.importo', 'Importo')}:</strong> {formatAmount(instalment.amount)}</Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2"><strong>{t('rata.scadenza', 'Scadenza prevista')}:</strong> {formatDate(instalment.due_date)}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2"><strong>{t('paga.stato', 'Stato')}:</strong> {instalmentStatusLabel(instalment.payment_status)}</Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {instalment.payment_status === 'paid' ? (
              <Alert severity="success">{t('rata.gia_pagata', 'Questa rata è già stata pagata.')}</Alert>
            ) : instalment.payment_status === 'canceled' ? (
              <Alert severity="warning">{t('rata.annullata', 'Questa rata è stata annullata.')}</Alert>
            ) : instalment.payment_method === 'bonifico' ? (
              <Alert severity="info">
                {t('rata.bonifico', "Questa rata è da pagare tramite bonifico bancario. Controlla l'email che hai ricevuto per IBAN, causale e importo.")}
              </Alert>
            ) : (
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handlePay}
                  disabled={submitting}
                >
                  {submitting ? <CircularProgress size={22} /> : t('rata.paga_ora', 'Paga ora {{importo}}', { importo: formatAmount(instalment.amount) })}
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default PayInstalmentPage;
